// Phase 29.6.3 · Management Exception Reporting.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertOctagon, Loader2, PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const RULE_LABELS = {
  exception_late_no_attendance: "Late / No Attendance",
  exception_gps_mismatch: "GPS Mismatch",
  exception_non_pjp_working: "Non-PJP Working",
  exception_beat_deviation: "Beat Deviation",
  exception_outlets_not_visited: "Outlets Not Visited",
  exception_zero_billing: "Zero Billing",
  exception_low_calls_productivity: "Low Calls / Productivity",
  exception_focus_sku_missed: "Focus SKU Missed",
  exception_target_below_threshold: "Target Below Threshold",
};

export default function ExceptionsPanel() {
  const [items, setItems] = useState(null);
  const [ruleCodes, setRuleCodes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [role, setRole] = useState(null);
  const nav = useNavigate();

  const load = () => {
    setBusy(true);
    const params = {};
    if (filter !== "all") params.type = filter;
    api.get("/exceptions", { params })
      .then((r) => {
        setItems(r.data.items || []);
        setRuleCodes(r.data.rule_codes || []);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  useEffect(() => {
    api.get("/auth/me").then((r) => setRole(r.data?.user?.role_code)).catch(() => {});
  }, []);

  const runDetect = async () => {
    setDetecting(true);
    try {
      const r = await api.post("/exceptions/detect", {});
      toast.success(
        `Detection complete · scanned ${r.data.users_scanned} SEs · `
        + `${r.data.notifications_created} new notifications`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDetecting(false);
    }
  };

  const byType = useMemo(() => {
    const m = {};
    (items || []).forEach((n) => {
      m[n.type] = (m[n.type] || 0) + 1;
    });
    return m;
  }, [items]);

  const canRunDetect = role && ["SUPER_ADMIN", "MANAGEMENT",
                                  "MIS", "ZSM", "ASM", "RSM"].includes(role);

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="exc-panel-root">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-400">
            Management
          </div>
          <h1 className="text-2xl font-extrabold text-neutral-900">
            Exceptions
          </h1>
        </div>
        {canRunDetect && (
          <Button size="sm" onClick={runDetect} disabled={detecting}
                  data-testid="exc-run-detect-btn"
                  className="tulsi-gradient text-white h-9 rounded-lg">
            {detecting
              ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              : <PlayCircle className="h-4 w-4 mr-1.5" />}
            Run detection now
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <FilterChip type="all" label="All" count={items?.length || 0}
                     active={filter === "all"}
                     onClick={() => setFilter("all")} />
        {ruleCodes.map((rc) => (
          <FilterChip key={rc} type={rc} label={RULE_LABELS[rc] || rc}
                       count={byType[rc] || 0}
                       active={filter === rc}
                       onClick={() => setFilter(rc)} />
        ))}
      </div>

      {busy && !items && (
        <div className="py-8 text-center text-neutral-400">
          <Loader2 className="h-5 w-5 inline animate-spin" />
        </div>
      )}

      {items && items.length === 0 && (
        <Card className="border-dashed border-neutral-300 rounded-2xl"
              data-testid="exc-empty">
          <CardContent className="p-6 text-center text-sm text-neutral-500">
            No exceptions in scope. Try "Run detection now" for today.
          </CardContent>
        </Card>
      )}

      {items && items.map((n) => (
        <Card key={n.id}
              className="border-neutral-200 rounded-2xl cursor-pointer hover:border-neutral-400"
              data-testid="exc-card"
              data-type={n.type}
              data-recipient-user-id={n.recipient_user_id || n.user_id}
              data-date={n.payload?.date || (n.ts || "").slice(0, 10)}
              onClick={() => nav(`/management/exceptions/${n.id}`)}>
          <CardContent className="p-4 flex items-start gap-3">
            <AlertOctagon className="h-4 w-4 text-red-600 mt-1" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-neutral-900">
                {RULE_LABELS[n.type] || n.type}
              </div>
              <div className="text-[11px] text-neutral-500 font-mono">
                {n.payload?.date || (n.ts || "").slice(0, 10)}
                {n.payload?.se_name ? ` · ${n.payload.se_name}` : ""}
              </div>
              <div className="text-xs text-neutral-700 mt-1 truncate">
                {n.body}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function FilterChip({ type, label, count, active, onClick }) {
  return (
    <button onClick={onClick}
            data-testid="exc-filter-chip"
            data-type={type}
            className={`text-xs px-3 py-1 rounded-full border ${
              active
                ? "bg-neutral-900 text-white border-neutral-900"
                : "border-neutral-300 text-neutral-600"
            }`}>
      {label} · {count}
    </button>
  );
}
